import { useEffect, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { ApiError, api } from '../api';
import { deploymentModeLabel, environmentKindLabel, humanize, rollbackPolicyLabel } from '../labels';
import type {
  AuthKind,
  DeploymentStatus,
  MagentoRestoreVerifyResult,
  MediaRestoreVerifyResult,
  MigrationOutcome,
  RecoveryAction,
  ServerConnectionVerification,
  SyncMagentoDatabaseResult,
} from '../api';
import { useResource } from '../hooks';
import { Terminal } from '../components/Terminal';
import { ServerAuditCard } from '../components/ServerAuditCard';
import { SecuritySweepCard } from '../components/SecuritySweepCard';
import { MagentoOperationsCard } from '../components/MagentoOperationsCard';
import { CdnAuditCard } from '../components/CdnAuditCard';
import { PerformanceBenchmarkCard } from '../components/PerformanceBenchmarkCard';
import { CacheControlCard } from '../components/CacheControlCard';
import { MagentoConfigAuditCard } from '../components/MagentoConfigAuditCard';
import { MagentoIntegrationMonitorCard } from '../components/MagentoIntegrationMonitorCard';
import { DatabasePreflightCard } from '../components/DatabasePreflightCard';
import { ProjectHeader } from '../components/ProjectHeader';
import { FactList } from '../components/project/ui';
import { Callout, Checkbox, Input, Section, Select, Subsection, Table, Textarea, clickableRow } from '../components/primitives';
import { SshStat, SshStats } from '../components/SshStats';
import {
  ConfirmDialog,
  ErrorNotice,
  Field,
  LoadingRows,
  PlanStepList,
  SkeletonPage,
  StatusBadge,
} from '../components/ui';

const recoveryActionLabels: Record<RecoveryAction, string> = {
  restored_from_backup: 'Restored from a backup',
  verified_manual_fix: 'Verified a manual fix',
  accepted_data_loss: 'Accepted the data loss',
};

const authKindLabels: Record<AuthKind, string> = {
  ssh_password: 'Password',
  ssh_private_key: 'Private key',
};

/**
 * The Hetzner server's lifecycle has no status kind of its own: provisioning
 * is work in progress, running is healthy, failed needs action, deleted is
 * gone. The words stay the server's own.
 */
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

const serverVerificationReasonLabels: Record<string, string> = {
  unreachable: 'Commerce Ops could not reach the server on that port. Check the hostname and port, that SSH is running, and that the firewall allows the connection.',
  auth_failed: 'The server refused the username or credential. Check the SSH user and that the key or password is the one the server expects.',
  unexpected_response: 'Commerce Ops signed in, but the server did not answer the check command as expected. The user may not have a normal shell.',
  host_key_changed: 'The server identified itself with a different host key than on its first connection, so Commerce Ops did not sign in. If the server was rebuilt or its SSH keys were replaced, save the connection again to trust the new key; otherwise treat it as a possible interception.',
};

const outcomeLabels: Record<MigrationOutcome, string> = {
  not_run: 'no migration was applied',
  backward_compatible: 'migration is compatible with the previous release',
  incompatible: 'previous release cannot run against this schema',
  unknown: 'migration result could not be determined',
};

/** splitList turns a comma-separated field into a trimmed, non-empty list —
 * the shape the deliberately small template configuration surface expects. */
function splitList(value: string): string[] {
  return value
    .split(',')
    .map((item) => item.trim())
    .filter((item) => item !== '');
}

export type OperationsTab =
  | 'all'
  | 'deployments'
  | 'operations'
  | 'integrations'
  | 'health'
  | 'terminal'
  | 'database'
  | 'settings';

export function EnvironmentPage() {
  const { environmentId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab') as OperationsTab | null;
  const activeTab: OperationsTab =
    rawTab &&
    ['all', 'deployments', 'operations', 'integrations', 'health', 'terminal', 'database', 'settings'].includes(rawTab)
      ? rawTab
      : 'all';

  const isTab = (t: OperationsTab) => activeTab === 'all' || activeTab === t;

  const environment = useResource(() => api.getEnvironment(environmentId ?? ''), [environmentId], undefined, { key: `env:${environmentId}:record` });
  const [reloadToken, setReloadToken] = useState(0);
  const deployments = useResource(
    () => api.listDeployments(environmentId ?? ''),
    [environmentId, reloadToken], undefined, { key: `env:${environmentId}:legacy-deployments` },
  );

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const detail = useResource(
    () => (selectedId ? api.getDeployment(selectedId) : Promise.resolve(null)),
    [selectedId, reloadToken],
  );

  const [preflightReloadToken, setPreflightReloadToken] = useState(0);
  const preflight = useResource(
    () => (selectedId ? api.getDatabasePreflight(selectedId).catch(() => null) : Promise.resolve(null)),
    [selectedId, reloadToken, preflightReloadToken],
  );

  // While a worker job for the selected attempt is in flight or awaiting approval,
  // poll for updates so its progress shows up without a manual refresh.
  const selectedJobStatus = detail.data?.job?.status;
  const selectedDeploymentStatus = detail.data?.deployment?.status;
  useEffect(() => {
    if (selectedJobStatus !== 'queued' && selectedJobStatus !== 'leased' && selectedDeploymentStatus !== 'awaiting_db_approval') return;
    const interval = setInterval(() => {
      setReloadToken((token) => token + 1);
      setPreflightReloadToken((token) => token + 1);
    }, 2000);
    return () => clearInterval(interval);
  }, [selectedId, selectedJobStatus, selectedDeploymentStatus]);

  const [templateReloadToken, setTemplateReloadToken] = useState(0);
  const template = useResource(
    () => api.getAtomicTemplate(environmentId ?? ''),
    [environmentId, templateReloadToken], undefined, { key: `env:${environmentId}:atomic-template` },
  );
  const [appRoot, setAppRoot] = useState('');
  const [releaseRoot, setReleaseRoot] = useState('');
  const [phpBinary, setPhpBinary] = useState('/usr/bin/php');
  const [sharedPaths, setSharedPaths] = useState('');
  const [smokeTestPaths, setSmokeTestPaths] = useState('');
  const [templateError, setTemplateError] = useState<Error | null>(null);
  const [savingTemplate, setSavingTemplate] = useState(false);

  // Reflect the server's stored configuration, never a client-invented guess.
  useEffect(() => {
    const config = template.data?.config;
    if (!config) return;
    setAppRoot(config.application_root);
    setReleaseRoot(config.release_root);
    setPhpBinary(config.php_binary);
    setSharedPaths((config.shared_paths ?? []).join(', '));
    setSmokeTestPaths((config.smoke_test_paths ?? []).join(', '));
  }, [template.data]);

  const [connectionReloadToken, setConnectionReloadToken] = useState(0);
  const serverConnection = useResource(
    () => api.getServerConnection(environmentId ?? ''),
    [environmentId, connectionReloadToken], undefined, { key: `env:${environmentId}:server-connection` },
  );
  const [host, setHost] = useState('');
  const [port, setPort] = useState(22);
  const [username, setUsername] = useState('');
  const [authKind, setAuthKind] = useState<AuthKind>('ssh_password');
  const [secret, setSecret] = useState('');
  const [connectionError, setConnectionError] = useState<Error | null>(null);
  const [savingConnection, setSavingConnection] = useState(false);
  const [removingConnection, setRemovingConnection] = useState(false);
  const [verifyingConnection, setVerifyingConnection] = useState(false);
  const [connectionVerifyResult, setConnectionVerifyResult] = useState<ServerConnectionVerification | null>(null);
  const [connectionVerifyError, setConnectionVerifyError] = useState<Error | null>(null);

  // Reflect the server's stored connection metadata. The secret itself is
  // never returned, so the field always starts empty: leaving it blank on
  // "Update connection" would be wrong (it would try to save an empty
  // secret), so the form always requires re-entering it to save again.
  useEffect(() => {
    const connection = serverConnection.data?.connection;
    if (!connection) return;
    setHost(connection.host);
    setPort(connection.port);
    setUsername(connection.username);
    setAuthKind(connection.auth_kind);
  }, [serverConnection.data]);

  async function handleConfigureConnection(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!environmentId) return;
    setSavingConnection(true);
    setConnectionError(null);
    try {
      await api.configureServerConnection(environmentId, {
        host: host.trim(),
        port,
        username: username.trim(),
        auth_kind: authKind,
        secret,
      });
      setSecret('');
      setConnectionReloadToken((value) => value + 1);
    } catch (cause) {
      setConnectionError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setSavingConnection(false);
    }
  }

  const [confirmingRemoveConnection, setConfirmingRemoveConnection] = useState(false);

  async function handleRemoveConnection() {
    if (!environmentId) return;
    setRemovingConnection(true);
    setConnectionError(null);
    try {
      await api.deleteServerConnection(environmentId);
      setHost('');
      setUsername('');
      setSecret('');
      setConnectionVerifyResult(null);
      setConnectionReloadToken((value) => value + 1);
    } catch (cause) {
      setConnectionError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setRemovingConnection(false);
      setConfirmingRemoveConnection(false);
    }
  }

  async function handleVerifyConnection() {
    if (!environmentId) return;
    setVerifyingConnection(true);
    setConnectionVerifyError(null);
    setConnectionVerifyResult(null);
    try {
      setConnectionVerifyResult(await api.verifyServerConnection(environmentId));
    } catch (cause) {
      setConnectionVerifyError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setVerifyingConnection(false);
    }
  }

  // getManagedServer 404s ("not_found") for an environment that has never
  // had one provisioned — a normal state (every fresh managed_docker
  // environment starts here), not an error to surface. Fetched
  // unconditionally like template/serverConnection above; the card that
  // renders it is gated on deployment_mode, not this fetch itself.
  const [managedServerReloadToken, setManagedServerReloadToken] = useState(0);
  const managedServer = useResource(
    () => api.getManagedServer(environmentId ?? ''),
    [environmentId, managedServerReloadToken], undefined, { key: `env:${environmentId}:managed-server` },
  );
  const managedServerNotFound =
    managedServer.error instanceof ApiError && managedServer.error.code === 'not_found';
  // Provisioning a new Hetzner server from this page was removed: that
  // SSH-bootstrapped path is superseded by connecting a server through the
  // Commerce Ops Agent (docs/ENVIRONMENT_ARCHITECTURE.md §5). A server that
  // was created by it earlier is still shown and can be deleted.
  const [pendingDeprovisionManagedServer, setPendingDeprovisionManagedServer] = useState(false);
  const [deprovisioningManagedServer, setDeprovisioningManagedServer] = useState(false);
  const [deprovisionManagedServerError, setDeprovisionManagedServerError] = useState<Error | null>(null);

  async function confirmDeprovisionManagedServer() {
    if (!managedServer.data) return;
    setDeprovisioningManagedServer(true);
    setDeprovisionManagedServerError(null);
    try {
      await api.deprovisionManagedServer(managedServer.data.id);
      setManagedServerReloadToken((value) => value + 1);
    } catch (cause) {
      // Closes the dialog so the card's own ErrorNotice (behind the modal
      // while it's open) becomes visible again.
      setDeprovisionManagedServerError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setDeprovisioningManagedServer(false);
      setPendingDeprovisionManagedServer(false);
    }
  }

  const [liveExecutionConfirmName, setLiveExecutionConfirmName] = useState('');
  const [settingLiveExecution, setSettingLiveExecution] = useState(false);
  const [liveExecutionError, setLiveExecutionError] = useState<Error | null>(null);

  async function handleSetLiveExecution(nextEnabled: boolean) {
    if (!environmentId) return;
    setSettingLiveExecution(true);
    setLiveExecutionError(null);
    try {
      await api.setLiveExecution(environmentId, nextEnabled, liveExecutionConfirmName);
      setLiveExecutionConfirmName('');
      environment.reload();
    } catch (cause) {
      setLiveExecutionError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setSettingLiveExecution(false);
    }
  }

  const [backupReloadToken, setBackupReloadToken] = useState(0);
  const backups = useResource(() => api.listDatabaseBackups(environmentId ?? ''), [environmentId, backupReloadToken]);
  const [backupLabel, setBackupLabel] = useState('');
  const [backupError, setBackupError] = useState<Error | null>(null);
  const [retentionDays, setRetentionDays] = useState(14);
  const [savingRetention, setSavingRetention] = useState(false);

  useEffect(() => {
    if (environment.data) setRetentionDays(environment.data.backup_retention_days);
  }, [environment.data]);

  const [creatingMagentoBackup, setCreatingMagentoBackup] = useState(false);
  const [magentoBackupError, setMagentoBackupError] = useState<Error | null>(null);

  async function handleCreateMagentoBackup() {
    if (!environmentId) return;
    setCreatingMagentoBackup(true);
    setMagentoBackupError(null);
    try {
      await api.createMagentoDatabaseBackup(environmentId, { label: backupLabel.trim() });
      setBackupLabel('');
      setBackupReloadToken((value) => value + 1);
    } catch (cause) {
      setMagentoBackupError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setCreatingMagentoBackup(false);
    }
  }

  const [verifyingMagentoBackupID, setVerifyingMagentoBackupID] = useState<string | null>(null);
  const [magentoRestoreVerifyResults, setMagentoRestoreVerifyResults] = useState<Record<string, MagentoRestoreVerifyResult>>({});
  const [magentoRestoreVerifyErrors, setMagentoRestoreVerifyErrors] = useState<Record<string, Error>>({});

  async function handleVerifyMagentoRestore(backupId: string) {
    if (!environmentId) return;
    setVerifyingMagentoBackupID(backupId);
    setMagentoRestoreVerifyErrors((prev) => {
      const next = { ...prev };
      delete next[backupId];
      return next;
    });
    try {
      const result = await api.verifyMagentoDatabaseBackupRestore(environmentId, backupId);
      setMagentoRestoreVerifyResults((prev) => ({ ...prev, [backupId]: result }));
    } catch (cause) {
      const error = cause instanceof Error ? cause : new Error(String(cause));
      setMagentoRestoreVerifyErrors((prev) => ({ ...prev, [backupId]: error }));
    } finally {
      setVerifyingMagentoBackupID(null);
    }
  }

  const [mediaBackupReloadToken, setMediaBackupReloadToken] = useState(0);
  const mediaBackups = useResource(() => api.listMediaBackups(environmentId ?? ''), [environmentId, mediaBackupReloadToken]);
  const [mediaBackupLabel, setMediaBackupLabel] = useState('');
  const [creatingMediaBackup, setCreatingMediaBackup] = useState(false);
  const [mediaBackupError, setMediaBackupError] = useState<Error | null>(null);

  async function handleCreateMediaBackup(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!environmentId) return;
    setCreatingMediaBackup(true);
    setMediaBackupError(null);
    try {
      await api.createMediaBackup(environmentId, { label: mediaBackupLabel.trim() });
      setMediaBackupLabel('');
      setMediaBackupReloadToken((value) => value + 1);
    } catch (cause) {
      setMediaBackupError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setCreatingMediaBackup(false);
    }
  }

  const [verifyingMediaBackupID, setVerifyingMediaBackupID] = useState<string | null>(null);
  const [mediaRestoreVerifyResults, setMediaRestoreVerifyResults] = useState<Record<string, MediaRestoreVerifyResult>>({});
  const [mediaRestoreVerifyErrors, setMediaRestoreVerifyErrors] = useState<Record<string, Error>>({});

  async function handleVerifyMediaRestore(backupId: string) {
    if (!environmentId) return;
    setVerifyingMediaBackupID(backupId);
    setMediaRestoreVerifyErrors((prev) => {
      const next = { ...prev };
      delete next[backupId];
      return next;
    });
    try {
      const result = await api.verifyMediaBackupRestore(environmentId, backupId);
      setMediaRestoreVerifyResults((prev) => ({ ...prev, [backupId]: result }));
    } catch (cause) {
      const error = cause instanceof Error ? cause : new Error(String(cause));
      setMediaRestoreVerifyErrors((prev) => ({ ...prev, [backupId]: error }));
    } finally {
      setVerifyingMediaBackupID(null);
    }
  }

  async function handleSaveRetention() {
    if (!environmentId) return;
    setSavingRetention(true);
    setBackupError(null);
    try {
      await api.setBackupPolicy(environmentId, { retention_days: retentionDays });
    } catch (cause) {
      setBackupError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setSavingRetention(false);
    }
  }

  const [smokeCheckReloadToken, setSmokeCheckReloadToken] = useState(0);
  const smokeChecks = useResource(
    () => api.listSmokeChecks(environmentId ?? ''),
    [environmentId, smokeCheckReloadToken],
  );
  const [smokeCheckPath, setSmokeCheckPath] = useState('');
  const [smokeCheckLabel, setSmokeCheckLabel] = useState('');
  const [smokeCheckExpectedStatus, setSmokeCheckExpectedStatus] = useState(200);
  const [creatingSmokeCheck, setCreatingSmokeCheck] = useState(false);
  const [smokeCheckError, setSmokeCheckError] = useState<Error | null>(null);
  const [runningSmokeChecks, setRunningSmokeChecks] = useState(false);

  async function handleCreateSmokeCheck(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!environmentId) return;
    setCreatingSmokeCheck(true);
    setSmokeCheckError(null);
    try {
      await api.createSmokeCheck(environmentId, {
        path: smokeCheckPath.trim(),
        label: smokeCheckLabel.trim(),
        expected_status: smokeCheckExpectedStatus,
      });
      setSmokeCheckPath('');
      setSmokeCheckLabel('');
      setSmokeCheckReloadToken((value) => value + 1);
    } catch (cause) {
      setSmokeCheckError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setCreatingSmokeCheck(false);
    }
  }

  const [pendingSmokeCheckDelete, setPendingSmokeCheckDelete] = useState<{ id: string; label: string } | null>(null);
  const [deletingSmokeCheck, setDeletingSmokeCheck] = useState(false);

  async function handleDeleteSmokeCheck(checkId: string) {
    setSmokeCheckError(null);
    setDeletingSmokeCheck(true);
    try {
      await api.deleteSmokeCheck(checkId);
      setSmokeCheckReloadToken((value) => value + 1);
    } catch (cause) {
      setSmokeCheckError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setDeletingSmokeCheck(false);
      setPendingSmokeCheckDelete(null);
    }
  }

  async function handleRunSmokeChecks() {
    if (!environmentId) return;
    setRunningSmokeChecks(true);
    setSmokeCheckError(null);
    try {
      await api.runSmokeChecks(environmentId, selectedId ? { deployment_id: selectedId } : {});
      refreshAll();
    } catch (cause) {
      setSmokeCheckError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setRunningSmokeChecks(false);
    }
  }

  const [recoveryAction, setRecoveryAction] = useState<RecoveryAction>('verified_manual_fix');
  const [recoveryBackupId, setRecoveryBackupId] = useState('');
  const [recoveryNotes, setRecoveryNotes] = useState('');

  const workspace = useResource(api.workspace, [], undefined, { key: 'workspace' });
  const identity = workspace.data?.identity;
  const canAccessTerminal =
    identity?.role?.is_owner_role ||
    identity?.capabilities?.includes('manage_terminal') ||
    identity?.kind === 'dev_workspace';
  const canManageDeployments =
    identity?.role?.is_owner_role ||
    identity?.capabilities?.includes('manage_deployments') ||
    identity?.kind === 'dev_workspace';

  const [healthReloadToken, setHealthReloadToken] = useState(0);
  const health = useResource(
    () => (environmentId && serverConnection.data?.configured ? api.getMagentoHealth(environmentId) : Promise.resolve(null)),
    [environmentId, serverConnection.data?.configured, healthReloadToken],
  );

  const siblingEnvironments = useResource(
    () => (environment.data?.project_id ? api.listEnvironments(environment.data.project_id) : Promise.resolve({ environments: [] })),
    [environment.data?.project_id],
  );

  const project = useResource(
    () => (environment.data?.project_id ? api.getProject(environment.data.project_id) : Promise.resolve(null)),
    [environment.data?.project_id],
  );

  const [syncSourceEnvId, setSyncSourceEnvId] = useState('');
  const [syncSourceBackupId, setSyncSourceBackupId] = useState('');
  const [syncTargetBaseUrl, setSyncTargetBaseUrl] = useState('');
  const [syncAnonymizePII, setSyncAnonymizePII] = useState(true);
  const [syncDisableEmails, setSyncDisableEmails] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<SyncMagentoDatabaseResult | null>(null);
  const [syncError, setSyncError] = useState<Error | null>(null);

  // Syncing replaces this environment's database, so the form only asks;
  // the confirmation runs it.
  const [confirmingSync, setConfirmingSync] = useState(false);

  async function handleSyncDatabase() {
    if (!environmentId) return;
    setSyncing(true);
    setSyncError(null);
    setSyncResult(null);
    try {
      const res = await api.syncMagentoDatabase(environmentId, {
        source_environment_id: syncSourceEnvId || undefined,
        source_backup_id: syncSourceBackupId || undefined,
        target_base_url: syncTargetBaseUrl.trim() || undefined,
        anonymize_pii: syncAnonymizePII,
        disable_emails: syncDisableEmails,
        // A production database is replaced only when its name was typed (RES-048).
        confirm_target: env?.kind === 'production' ? env.name : undefined,
      });
      setSyncResult(res);
      setBackupReloadToken((t) => t + 1);
      setHealthReloadToken((t) => t + 1);
    } catch (cause) {
      setSyncError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setSyncing(false);
      setConfirmingSync(false);
    }
  }
  const [reconciling, setReconciling] = useState(false);
  const [recoveryError, setRecoveryError] = useState<Error | null>(null);

  async function handleReconcileRecovery(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedId) return;
    setReconciling(true);
    setRecoveryError(null);
    try {
      await api.reconcileRecovery(selectedId, {
        action: recoveryAction,
        ...(recoveryAction === 'restored_from_backup' && recoveryBackupId ? { backup_id: recoveryBackupId } : {}),
        notes: recoveryNotes.trim(),
      });
      setRecoveryNotes('');
      refreshAll();
    } catch (cause) {
      setRecoveryError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setReconciling(false);
    }
  }

  const [commitSHA, setCommitSHA] = useState('');
  const [commitRef, setCommitRef] = useState('refs/heads/main');
  const [commitMessage, setCommitMessage] = useState('');
  const [startError, setStartError] = useState<Error | null>(null);
  const [starting, setStarting] = useState(false);

  const [toStatus, setToStatus] = useState<DeploymentStatus | ''>('');
  const [outcome, setOutcome] = useState<MigrationOutcome | ''>('');
  const [transitionMessage, setTransitionMessage] = useState('');
  const [transitionError, setTransitionError] = useState<Error | null>(null);

  function refreshAll() {
    setReloadToken((value) => value + 1);
  }

  async function handleStart(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!environmentId) return;
    setStarting(true);
    setStartError(null);
    try {
      const created = await api.startDeployment(environmentId, {
        commit_sha: commitSHA.trim(),
        commit_ref: commitRef.trim(),
        commit_message: commitMessage.trim(),
      });
      setSelectedId(created.deployment.id);
      setCommitMessage('');
      refreshAll();
    } catch (cause) {
      setStartError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setStarting(false);
    }
  }

  async function handleConfigureTemplate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!environmentId) return;
    setSavingTemplate(true);
    setTemplateError(null);
    try {
      await api.configureAtomicTemplate(environmentId, {
        application_root: appRoot.trim(),
        release_root: releaseRoot.trim(),
        php_binary: phpBinary.trim(),
        shared_paths: splitList(sharedPaths),
        smoke_test_paths: splitList(smokeTestPaths),
      });
      setTemplateReloadToken((value) => value + 1);
    } catch (cause) {
      setTemplateError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setSavingTemplate(false);
    }
  }

  async function handleTransition(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedId || toStatus === '') return;
    setTransitionError(null);
    try {
      await api.transitionDeployment(selectedId, {
        to_status: toStatus,
        ...(outcome === '' ? {} : { migration_outcome: outcome }),
        ...(transitionMessage.trim() === '' ? {} : { message: transitionMessage.trim() }),
      });
      setToStatus('');
      setOutcome('');
      setTransitionMessage('');
      refreshAll();
    } catch (cause) {
      setTransitionError(cause instanceof Error ? cause : new Error(String(cause)));
    }
  }


  if (environment.loading) return <SkeletonPage cards={2} rows={4} />;
  if (environment.error) return <ErrorNotice error={environment.error} />;
  const env = environment.data;
  if (!env) return null;

  const attempts = deployments.data?.deployments ?? [];
  const selected = detail.data;
  // Live execution, and the Magento-specific database/media backup
  // actions below, work identically for both modes once configured: for
  // existing_atomic the customer configures the atomic template
  // themselves; for managed_docker, bootstrapManagedDockerEnvironment
  // configures the same real AtomicDeploymentConfig (and a real
  // ServerConnection) automatically once provisioning succeeds — see
  // SetLiveExecutionEnabled's own doc comment. Gating this UI on
  // existing_atomic alone left every managed_docker environment unable
  // to ever reach the feature it had just been bootstrapped to support.
  // Which page this is depends on how the environment is deployed. An
  // agent-run environment ("Commerce Ops managed stack") is operated from
  // the project page — its server placement, deployments, backups and
  // monitors all live there — so this page only shows its settings and any
  // legacy managed-server record. Everything else below (SSH connection,
  // atomic template, live execution, attempts, health probes, staging
  // sync, terminal) is the SSH path for an existing Magento server.
  const isSSHEnvironment = env.deployment_mode === 'existing_atomic';
  const supportsMagentoServerFeatures = isSSHEnvironment;

  const tabs = isSSHEnvironment
    ? [
        { id: 'all', label: 'Everything', active: activeTab === 'all', onClick: () => setSearchParams({ tab: 'all' }) },
        { id: 'deployments', label: 'Deployments', count: attempts.length, active: activeTab === 'deployments', onClick: () => setSearchParams({ tab: 'deployments' }) },
        { id: 'operations', label: 'Magento operations', active: activeTab === 'operations', onClick: () => setSearchParams({ tab: 'operations' }) },
        { id: 'integrations', label: 'Integrations', active: activeTab === 'integrations', onClick: () => setSearchParams({ tab: 'integrations' }) },
        { id: 'health', label: 'Health checks', active: activeTab === 'health', onClick: () => setSearchParams({ tab: 'health' }) },
        ...(serverConnection.data?.configured && canAccessTerminal
          ? [{ id: 'terminal', label: 'SSH terminal', active: activeTab === 'terminal', onClick: () => setSearchParams({ tab: 'terminal' }) }]
          : []),
        { id: 'database', label: 'Backups and staging sync', active: activeTab === 'database', onClick: () => setSearchParams({ tab: 'database' }) },
        { id: 'settings', label: 'Server and settings', active: activeTab === 'settings', onClick: () => setSearchParams({ tab: 'settings' }) },
      ]
    : [];

  const latestStatus = attempts[0]?.deployment?.status;

  // The Hetzner server's facts, shown in both branches below.
  const managedServerFacts = (server: NonNullable<typeof managedServer.data>) => {
    const facts: { label: string; value: ReactNode }[] = [
      { label: 'Status', value: <StatusBadge kind="managedServer" value={server.status} /> },
    ];
    if (server.public_ipv4) facts.push({ label: 'Public IPv4', value: <code>{server.public_ipv4}</code> });
    if (server.docker_version) facts.push({ label: 'Docker', value: server.docker_version });
    return facts;
  };

  if (!isSSHEnvironment) {
    // A managed environment is operated from its project; this page only
    // remains for one that still has a server from the retired Hetzner path
    // (audit N5).
    if (managedServerNotFound) {
      return <Navigate to={`/app/projects/${env.project_id}/settings?env=${encodeURIComponent(env.slug)}`} replace />;
    }
    if (managedServer.loading && !managedServer.data) return <SkeletonPage rows={2} />;
    return (
      <section>
        <ProjectHeader
          projectName={project.data?.project?.name ?? 'Project'}
          projectId={env.project_id}
          environmentName={env.name}
        />

        <div className="ssh-stack">
          <Section
            title={`${env.name} settings`}
            actions={
              <Link className="button" to={`/app/projects/${env.project_id}`}>
                Open {project.data?.project?.name ?? 'project'}
              </Link>
            }
          >
            <FactList
              facts={[
                { label: 'Type', value: environmentKindLabel[env.kind] ?? humanize(env.kind) },
                { label: 'URL slug', value: <code>{env.slug}</code> },
                {
                  label: 'How it is deployed',
                  value: deploymentModeLabel[env.deployment_mode],
                },
                { label: 'After a failed deployment', value: rollbackPolicyLabel[env.rollback_policy] ?? humanize(env.rollback_policy) },
              ]}
            />
          </Section>

          {managedServer.data ? (
            <Section
              title="Hetzner server created by Commerce Ops"
              actions={
                <button className="button button--danger-quiet" type="button" onClick={() => setPendingDeprovisionManagedServer(true)}>
                  Delete this Hetzner server
                </button>
              }
            >
              <FactList facts={managedServerFacts(managedServer.data)} />
              {managedServer.data.status === 'failed' && managedServer.data.failure_reason ? (
                <Callout tone="danger" title="Provisioning failed">
                  {managedServer.data.failure_reason}
                </Callout>
              ) : null}
              <ErrorNotice error={deprovisionManagedServerError} />
              <ConfirmDialog
                open={pendingDeprovisionManagedServer}
                title="Delete this Hetzner server?"
                description="The Hetzner server and everything on it — the database, every deployed release, all files — are deleted. This cannot be undone. Any environment still using it stops serving traffic."
                confirmLabel="Delete server"
                confirmText={managedServer.data.public_ipv4 || env.name}
                danger
                busy={deprovisioningManagedServer}
                onConfirm={confirmDeprovisionManagedServer}
                onCancel={() => setPendingDeprovisionManagedServer(false)}
              />
            </Section>
          ) : null}
        </div>
      </section>
    );
  }

  const smokeCheckCount = smokeChecks.data?.checks.length ?? 0;

  return (
    <section>
      <ProjectHeader
        projectName={project.data?.project?.name ?? 'Project'}
        projectId={env.project_id}
        environmentName={env.name}
        domain={serverConnection.data?.connection?.host}
        status={latestStatus}
        tabs={tabs}
      />

      <div className="ssh-stack">

        {isTab('settings') && (
          <>
            {managedServer.data ? (
              <Section
                title="Hetzner server created by Commerce Ops"
                actions={
                  <button className="button button--danger-quiet" type="button" onClick={() => setPendingDeprovisionManagedServer(true)}>
                    Delete this Hetzner server
                  </button>
                }
              >
                {managedServer.loading ? <LoadingRows label="Loading managed server…" rows={3} rowHeight="24px" /> : null}
                {managedServer.error && !managedServerNotFound ? <ErrorNotice error={managedServer.error} /> : null}
                <FactList
                  facts={[
                    ...managedServerFacts(managedServer.data),
                    ...(managedServer.data.status === 'running' && managedServer.data.magento_base_url
                      ? [
                          {
                            label: 'Serving at',
                            value: (
                              <>
                                <a href={managedServer.data.magento_base_url} target="_blank" rel="noreferrer">
                                  {managedServer.data.magento_base_url}
                                </a>
                                {managedServer.data.magento_admin_username ? (
                                  <div className="muted">
                                    Admin user {managedServer.data.magento_admin_username}
                                    {managedServer.data.magento_admin_created ? null : ' (created on the first deployment)'}
                                  </div>
                                ) : null}
                              </>
                            ),
                          },
                        ]
                      : []),
                  ]}
                />
                {managedServer.data.status === 'failed' && managedServer.data.failure_reason ? (
                  <Callout tone="danger" title="Provisioning failed">
                    {managedServer.data.failure_reason}
                  </Callout>
                ) : null}
                <ErrorNotice error={deprovisionManagedServerError} />
                <ConfirmDialog
                  open={pendingDeprovisionManagedServer}
                  title="Deprovision this managed server?"
                  description="This really deletes the Hetzner server and everything on it — the database, any deployed release, all of it. This cannot be undone. Frees this organization's one managed-server slot so a new one can be provisioned."
                  confirmLabel="Delete server"
                  danger
                  busy={deprovisioningManagedServer}
                  onConfirm={confirmDeprovisionManagedServer}
                  onCancel={() => setPendingDeprovisionManagedServer(false)}
                />
              </Section>
            ) : null}

            <Section
              title="Paths on the server"
            >
              {template.loading ? <LoadingRows label="Loading template…" rows={5} rowHeight="52px" /> : null}
              <ErrorNotice error={template.error} />
              {template.data && !template.data.available ? (
                <p className="quiet-empty">{template.data.reason ?? 'This environment does not use server paths.'}</p>
              ) : null}
              {template.data && template.data.available ? (
                <>
                  <form className="form" onSubmit={handleConfigureTemplate}>
                    <Field label="Application root" hint="Absolute path to the live document root">
                      <Input required value={appRoot} onChange={(event) => setAppRoot(event.target.value)} placeholder="/var/www/store" />
                    </Field>
                    <Field label="Release root" hint="Absolute path where release directories are built">
                      <Input
                        required
                        value={releaseRoot}
                        onChange={(event) => setReleaseRoot(event.target.value)}
                        placeholder="/var/www/releases"
                      />
                    </Field>
                    <Field label="PHP binary" hint="Absolute path ending in php">
                      <Input required value={phpBinary} onChange={(event) => setPhpBinary(event.target.value)} placeholder="/usr/bin/php" />
                    </Field>
                    <Field label="Shared paths" hint="Comma-separated relative paths kept across releases">
                      <Input value={sharedPaths} onChange={(event) => setSharedPaths(event.target.value)} placeholder="var, pub/media" />
                    </Field>
                    <Field label="Smoke test paths" hint="Comma-separated relative paths or public HTTPS URLs, up to 8">
                      <Input value={smokeTestPaths} onChange={(event) => setSmokeTestPaths(event.target.value)} placeholder="/health" />
                    </Field>
                    <ErrorNotice error={templateError} />
                    <button
                      className="button"
                      type="submit"
                      disabled={savingTemplate || appRoot.trim() === '' || releaseRoot.trim() === '' || phpBinary.trim() === ''}
                    >
                      {savingTemplate ? 'Saving' : template.data.configured ? 'Update template' : 'Save template'}
                    </button>
                  </form>

                  {template.data.configured && template.data.steps ? (
                    <Subsection
                      title={`Planned steps (${template.data.template_version})`}
                      description="Preview of the current template, not saved."
                    >
                      <PlanStepList steps={template.data.steps} />
                    </Subsection>
                  ) : null}
                </>
              ) : null}
            </Section>

            <Section
              title="SSH connection to the server"
              help={<p>The server’s host key is not pinned yet, so the first connection trusts the key it presents.</p>}
              helpLabel="Host key"
              actions={
                serverConnection.data?.configured && serverConnection.data.connection ? (
                  <button className="button button--secondary" type="button" onClick={handleVerifyConnection} disabled={verifyingConnection}>
                    {verifyingConnection ? 'Checking' : 'Check connection'}
                  </button>
                ) : undefined
              }
            >
              {serverConnection.loading ? <LoadingRows label="Loading connection…" rows={5} rowHeight="52px" /> : null}
              <ErrorNotice error={serverConnection.error} />
              {serverConnection.data && !serverConnection.data.available ? (
                <p className="quiet-empty">{serverConnection.data.reason ?? 'This environment does not use an SSH connection.'}</p>
              ) : null}
              {serverConnection.data && serverConnection.data.available ? (
                <>
                  {serverConnection.data.configured && serverConnection.data.connection ? (
                    <FactList
                      facts={[
                        { label: 'Status', value: <StatusBadge kind="connection" value="configured" /> },
                        {
                          label: 'Address',
                          value: (
                            <code>
                              {serverConnection.data.connection.username}@{serverConnection.data.connection.host}:
                              {serverConnection.data.connection.port}
                            </code>
                          ),
                        },
                        { label: 'Signs in with', value: authKindLabels[serverConnection.data.connection.auth_kind] },
                        ...(serverConnection.data.connection.created_by && serverConnection.data.connection.updated_at
                          ? [
                              {
                                label: 'Saved',
                                value: `By ${serverConnection.data.connection.created_by} on ${new Date(serverConnection.data.connection.updated_at).toLocaleString()}`,
                              },
                            ]
                          : []),
                      ]}
                    />
                  ) : (
                    <p className="summary-line">
                      <StatusBadge kind="connection" value="not_configured" />
                    </p>
                  )}
                  <ErrorNotice error={connectionVerifyError} />
                  {connectionVerifyResult ? (
                    connectionVerifyResult.verified ? (
                      <Callout tone="success" title="Connection works" />
                    ) : (
                      <Callout tone="danger" title="Connection failed">
                        {(connectionVerifyResult.reason && serverVerificationReasonLabels[connectionVerifyResult.reason]) ??
                          'Commerce Ops could not connect.'}
                      </Callout>
                    )
                  ) : null}
                  <form className="form" onSubmit={handleConfigureConnection}>
                    <Field label="Host" hint="Hostname or IP address">
                      <Input required value={host} onChange={(event) => setHost(event.target.value)} placeholder="203.0.113.10" />
                    </Field>
                    <Field label="Port">
                      <Input
                        required
                        type="number"
                        min={1}
                        max={65535}
                        value={port}
                        onChange={(event) => setPort(Number(event.target.value))}
                      />
                    </Field>
                    <Field label="Username">
                      <Input required value={username} onChange={(event) => setUsername(event.target.value)} placeholder="deploy" />
                    </Field>
                    <Field label="Authentication">
                      <Select value={authKind} onChange={(event) => setAuthKind(event.target.value as AuthKind)}>
                        {(Object.keys(authKindLabels) as AuthKind[]).map((value) => (
                          <option key={value} value={value}>
                            {authKindLabels[value]}
                          </option>
                        ))}
                      </Select>
                    </Field>
                    <Field
                      label={authKind === 'ssh_private_key' ? 'Private key' : 'Password'}
                      hint={
                        serverConnection.data.configured
                          ? 'Required to save again — the stored secret is never shown, so it cannot be kept unchanged.'
                          : undefined
                      }
                    >
                      {authKind === 'ssh_private_key' ? (
                        <Textarea
                          required
                          rows={6}
                          value={secret}
                          onChange={(event) => setSecret(event.target.value)}
                          placeholder="-----BEGIN OPENSSH PRIVATE KEY-----"
                        />
                      ) : (
                        <Input required type="password" value={secret} onChange={(event) => setSecret(event.target.value)} autoComplete="off" />
                      )}
                    </Field>
                    <ErrorNotice error={connectionError} />
                    <div className="ssh-actions">
                      <button
                        className="button"
                        type="submit"
                        disabled={savingConnection || host.trim() === '' || username.trim() === '' || secret === ''}
                      >
                        {savingConnection ? 'Saving' : serverConnection.data.configured ? 'Update connection' : 'Save connection'}
                      </button>
                      {serverConnection.data.configured ? (
                        <button
                          className="button button--danger-quiet"
                          type="button"
                          onClick={() => setConfirmingRemoveConnection(true)}
                          disabled={removingConnection}
                        >
                          Remove connection
                        </button>
                      ) : null}
                    </div>
                  </form>
                  <ConfirmDialog
                    open={confirmingRemoveConnection}
                    title="Remove the SSH connection?"
                    description="Commerce Ops forgets the host, user and credential of this environment. Deployments, backups, health checks and the terminal stop working here until a connection is saved again. Nothing on the server changes."
                    confirmLabel="Remove connection"
                    danger
                    busy={removingConnection}
                    onConfirm={handleRemoveConnection}
                    onCancel={() => setConfirmingRemoveConnection(false)}
                  />
                </>
              ) : null}
            </Section>

            {supportsMagentoServerFeatures ? (
              <Section
                title="Live execution"
                description={env.live_execution_enabled ? undefined : "Off: new deployments run as a rehearsal that changes nothing on the server."}
                help={
                  <p>
                    Turning this on makes every deployment started afterward run for real against the server connection
                    above — Commerce Ops builds the release, connects over SSH, and executes the maintenance window and
                    database migration for real. Only an owner can turn it on, and only after a fresh, successful
                    verification of both connections above, performed at that exact moment. This only affects
                    deployments started after the change — it does not stop or roll back one already running, and
                    turning it back off does not undo one that already ran for real.
                  </p>
                }
              >
                {env.live_execution_enabled ? (
                  <>
                    <p className="summary-line">
                      <StatusBadge kind="toggle" value="enabled" />
                    </p>
                    <button
                      className="button button--secondary"
                      type="button"
                      onClick={() => handleSetLiveExecution(false)}
                      disabled={settingLiveExecution}
                    >
                      {settingLiveExecution ? 'Disabling' : 'Disable live execution'}
                    </button>
                  </>
                ) : (
                  <>
                    <p className="summary-line">
                      <StatusBadge kind="toggle" value="disabled" />
                    </p>
                    <div className="ssh-inline-field">
                      <Field label="Type the environment name to confirm" hint={`Must exactly match "${env.name}".`}>
                        <Input
                          value={liveExecutionConfirmName}
                          onChange={(event) => setLiveExecutionConfirmName(event.target.value)}
                          placeholder={env.name}
                        />
                      </Field>
                    </div>
                    <button
                      className="button"
                      type="button"
                      onClick={() => handleSetLiveExecution(true)}
                      disabled={settingLiveExecution || liveExecutionConfirmName !== env.name}
                    >
                      {settingLiveExecution ? 'Enabling' : 'Enable live execution'}
                    </button>
                  </>
                )}
                <ErrorNotice error={liveExecutionError} />
              </Section>
            ) : null}
          </>
        )}

        {isTab('terminal') && supportsMagentoServerFeatures && serverConnection.data?.configured && canAccessTerminal ? (
          <Section
            title="SSH terminal"
            description="Everything you type runs as the SSH user."
          >
            <Terminal environmentId={env.id} environmentName={env.name} />
          </Section>
        ) : null}

        {isTab('health') && supportsMagentoServerFeatures && serverConnection.data?.configured && (
          <>
            <Section
              title="Cron, queues and disk"
              actions={
                <button
                  className="button button--secondary"
                  type="button"
                  onClick={() => setHealthReloadToken((t) => t + 1)}
                  disabled={health.loading}
                >
                  {health.loading ? 'Checking' : 'Refresh diagnostics'}
                </button>
              }
            >
              {health.loading ? <LoadingRows label="Running operational health probe…" rows={3} rowHeight="40px" /> : null}
              <ErrorNotice error={health.error} />
              {health.data ? (
                <SshStats testId="magento-health">
                  <SshStat
                    label="Cron heartbeat"
                    value={
                      <StatusBadge
                        kind="level"
                        value={health.data.cron_healthy ? 'ok' : 'warning'}
                        label={health.data.cron_healthy ? 'Active' : 'Stale or inactive'}
                      />
                    }
                    detail={
                      health.data.cron_heartbeat_age_sec >= 0
                        ? `Last run ${Math.round(health.data.cron_heartbeat_age_sec / 60)} min ago`
                        : 'No completed cron run found'
                    }
                  />
                  <SshStat
                    label="Stuck cron jobs (over 30 min)"
                    value={
                      <StatusBadge
                        kind="level"
                        value={health.data.stuck_job_count === 0 ? 'ok' : 'critical'}
                        label={`${health.data.stuck_job_count} stuck`}
                        className="tabular-nums"
                      />
                    }
                    detail={`${health.data.error_job_count} errors in 24 h`}
                  />
                  <SshStat
                    label="Queue backlog"
                    value={
                      <StatusBadge
                        kind="level"
                        value={health.data.queue_pending_count < 100 ? 'ok' : 'warning'}
                        label={`${health.data.queue_pending_count} pending`}
                        className="tabular-nums"
                      />
                    }
                    detail={`${health.data.queue_error_count} failed or retrying`}
                  />
                  <SshStat
                    label="Disk"
                    value={
                      <StatusBadge
                        kind="level"
                        value={health.data.disk_used_percent < 85 ? 'ok' : 'critical'}
                        label={`${health.data.disk_used_percent}% used`}
                        className="tabular-nums"
                      />
                    }
                    detail={`${formatBytes(health.data.disk_free_bytes)} free of ${formatBytes(health.data.disk_total_bytes)}`}
                  />
                </SshStats>
              ) : null}
              {health.data?.stuck_jobs && health.data.stuck_jobs.length > 0 ? (
                <Subsection title={`Stuck jobs (${health.data.stuck_jobs.length})`}>
                  <ul className="status-list status-list--stacked" aria-label="Stuck cron jobs">
                    {health.data.stuck_jobs.map((job) => (
                      <li key={job.schedule_id} className="status-list__item">
                        <code className="status-list__name">{job.job_code}</code>
                        <span className="status-list__detail">
                          Schedule #{job.schedule_id}, running for {Math.round(job.age_seconds / 60)} minutes (started{' '}
                          {new Date(job.executed_at).toLocaleString()}).
                        </span>
                      </li>
                    ))}
                  </ul>
                </Subsection>
              ) : null}
            </Section>

            <MagentoConfigAuditCard environmentId={env.id} />
            <div className="ssh-grid-2">
              <ServerAuditCard environmentId={env.id} />
              <SecuritySweepCard environmentId={env.id} />
            </div>
            <div className="ssh-grid-2">
              <CdnAuditCard environmentId={env.id} />
              <PerformanceBenchmarkCard environmentId={env.id} />
            </div>
          </>
        )}

        {isTab('integrations') && supportsMagentoServerFeatures && serverConnection.data?.configured && (
          <MagentoIntegrationMonitorCard environmentId={env.id} />
        )}

        {isTab('operations') && supportsMagentoServerFeatures && serverConnection.data?.configured && (
          <>
            <MagentoOperationsCard environmentId={env.id} />
            <CacheControlCard environmentId={env.id} />
          </>
        )}

        {isTab('database') && (
          <>
            <Section
              title="Database backups"
              help={
                <>
                  <p>
                    <strong>Back up this environment’s Magento database</strong> connects over SSH, reads the database
                    credentials from the active release’s <code>app/etc/env.php</code> on the server (they never leave
                    it), and streams a <code>mysqldump</code> back, encrypted, to object storage. Verifying a restore
                    loads the dump into a freshly created throwaway database on the same server, counts its tables and
                    rows, and drops it again — the live database is never touched.
                  </p>
                </>
              }
            >
              <div className="ssh-inline-field ssh-inline-field--narrow">
                <Field label="Retention (days)">
                  <Input
                    className="ssh-input-days"
                    type="number"
                    min={1}
                    max={365}
                    value={retentionDays}
                    onChange={(event) => setRetentionDays(Number(event.target.value))}
                  />
                </Field>
                <button className="button button--secondary" type="button" onClick={handleSaveRetention} disabled={savingRetention}>
                  {savingRetention ? 'Saving' : 'Save retention'}
                </button>
              </div>
              <form
                className="form"
                onSubmit={(event) => {
                  event.preventDefault();
                  void handleCreateMagentoBackup();
                }}
              >
                <Field label="Label" hint="Optional, for example pre-deploy">
                  <Input value={backupLabel} onChange={(event) => setBackupLabel(event.target.value)} />
                </Field>
                <ErrorNotice error={backupError} />
                <ErrorNotice error={magentoBackupError} />
                <div className="ssh-actions">
                  {supportsMagentoServerFeatures ? (
                    <button
                      className="button"
                      type="button"
                      disabled={creatingMagentoBackup || !serverConnection.data?.configured}
                      onClick={handleCreateMagentoBackup}
                      title={serverConnection.data?.configured ? undefined : 'Configure a server connection first'}
                    >
                      {creatingMagentoBackup ? 'Connecting over SSH' : 'Back up this environment’s Magento database'}
                    </button>
                  ) : null}
                </div>
              </form>
              {backups.loading ? <LoadingRows label="Loading backups…" rows={3} rowHeight="44px" /> : null}
              <ErrorNotice error={backups.error} />
              {(backups.data?.backups.length ?? 0) === 0 && !backups.loading ? (
                <p className="quiet-empty">No backups recorded yet.</p>
              ) : (
                <ul className="status-list status-list--stacked" aria-label="Database backups">
                  {backups.data?.backups.map((backup) => {
                    const isMagento = backup.source === 'magento_mysql';
                    const restoreResult = isMagento ? magentoRestoreVerifyResults[backup.id] : undefined;
                    const restoreError = isMagento ? magentoRestoreVerifyErrors[backup.id] : undefined;
                    const verifying = isMagento && verifyingMagentoBackupID === backup.id;
                    return (
                      <li key={backup.id} className="status-list__item">
                        <span className="status-list__name">
                          {backup.label || 'No label'}{' '}
                          <span className="ssh-meta">{isMagento ? 'Magento database' : 'Commerce Ops database'}</span>
                        </span>
                        <StatusBadge kind="backup" value={backup.status} />
                        <div className="status-list__detail">
                          {formatBytes(backup.size_bytes)}, by {backup.triggered_by}, {new Date(backup.created_at).toLocaleString()}. Expires{' '}
                          {new Date(backup.expires_at).toLocaleDateString()}.
                          {/* The platform database is backed up by Commerce Ops itself, not from a tenant page (RES-032). */}
                          {backup.status === 'completed' && isMagento ? (
                            <div className="ssh-actions">
                              <button
                                className="button button--secondary button--small"
                                type="button"
                                onClick={() => handleVerifyMagentoRestore(backup.id)}
                                disabled={verifying}
                              >
                                {verifying
                                  ? isMagento
                                    ? 'Restoring into a throwaway database on the real server'
                                    : 'Restoring into a throwaway database'
                                  : 'Verify restore'}
                              </button>
                              {restoreResult ? (
                                <span>
                                  <StatusBadge kind="backup" value="restored" />{' '}
                                  {`${restoreResult.table_count} tables, ${restoreResult.row_count} rows verified in a real throwaway database on the same server, then dropped`}
                                </span>
                              ) : null}
                            </div>
                          ) : null}
                          <ErrorNotice error={restoreError ?? null} />
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </Section>

            {supportsMagentoServerFeatures ? (
              <Section
                title="Media backups"
                help={
                  <p>
                    Verifying a restore safely extracts the archive into a bounded, throwaway local directory and reports
                    the real file and byte counts, then removes it again.
                  </p>
                }
              >
                <form className="form" onSubmit={handleCreateMediaBackup}>
                  <Field label="Label" hint="Optional, for example pre-deploy">
                    <Input value={mediaBackupLabel} onChange={(event) => setMediaBackupLabel(event.target.value)} />
                  </Field>
                  <ErrorNotice error={mediaBackupError} />
                  <button className="button" type="submit" disabled={creatingMediaBackup || !serverConnection.data?.configured}>
                    {creatingMediaBackup ? 'Archiving over SSH' : 'Take a media backup'}
                  </button>
                </form>
                {mediaBackups.loading ? <LoadingRows label="Loading media backups…" rows={2} rowHeight="44px" /> : null}
                <ErrorNotice error={mediaBackups.error} />
                {(mediaBackups.data?.backups.length ?? 0) === 0 && !mediaBackups.loading ? (
                  <p className="quiet-empty">No media backups recorded yet.</p>
                ) : (
                  <ul className="status-list status-list--stacked" aria-label="Media backups">
                    {mediaBackups.data?.backups.map((backup) => {
                      const result = mediaRestoreVerifyResults[backup.id];
                      return (
                        <li key={backup.id} className="status-list__item">
                          <span className="status-list__name">{backup.label || 'No label'}</span>
                          <StatusBadge kind="backup" value={backup.status} />
                          <div className="status-list__detail">
                            {formatBytes(backup.size_bytes)}, by {backup.triggered_by}, {new Date(backup.created_at).toLocaleString()}. Expires{' '}
                            {new Date(backup.expires_at).toLocaleDateString()}.
                            {backup.status === 'completed' ? (
                              <div className="ssh-actions">
                                <button
                                  className="button button--secondary button--small"
                                  type="button"
                                  onClick={() => handleVerifyMediaRestore(backup.id)}
                                  disabled={verifyingMediaBackupID === backup.id}
                                >
                                  {verifyingMediaBackupID === backup.id ? 'Extracting into a throwaway directory' : 'Verify restore'}
                                </button>
                                {result ? (
                                  <span>
                                    <StatusBadge kind="backup" value="extracted" /> {result.file_count} files ({formatBytes(result.total_bytes)})
                                    verified in a throwaway directory, then removed
                                  </span>
                                ) : null}
                              </div>
                            ) : null}
                            <ErrorNotice error={mediaRestoreVerifyErrors[backup.id] ?? null} />
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </Section>
            ) : null}

            {supportsMagentoServerFeatures && serverConnection.data?.configured ? (
              <Section
                title="Staging sync and anonymization"
                description="Replaces this environment’s database with a copy of another environment or a backup, rewrites the store base URLs and can mask customer data and turn off outgoing email."
              >
                <form
                  className="form"
                  onSubmit={(event) => {
                    event.preventDefault();
                    setConfirmingSync(true);
                  }}
                >
                  <Field label="Source environment" hint="The environment to copy, for example production">
                    <Select
                      value={syncSourceEnvId}
                      onChange={(e) => {
                        setSyncSourceEnvId(e.target.value);
                        if (e.target.value) setSyncSourceBackupId('');
                      }}
                    >
                      <option value="">Choose an environment</option>
                      {(siblingEnvironments.data?.environments ?? [])
                        .filter((e) => e.id !== env.id)
                        .map((e) => (
                          <option key={e.id} value={e.id}>
                            {e.name} ({e.slug})
                          </option>
                        ))}
                    </Select>
                  </Field>
                  <Field label="Or a backup ID" hint="Optional: copy a specific encrypted backup instead of the live environment">
                    <Input
                      value={syncSourceBackupId}
                      onChange={(e) => {
                        setSyncSourceBackupId(e.target.value);
                        if (e.target.value) setSyncSourceEnvId('');
                      }}
                      placeholder="Backup UUID, for example 550e8400-e29b-41d4-a716-446655440000"
                    />
                  </Field>
                  <Field
                    label="Store base URL of this environment"
                    hint="For example https://staging.example.com. Updates web/unsecure/base_url and web/secure/base_url."
                  >
                    <Input
                      value={syncTargetBaseUrl}
                      onChange={(e) => setSyncTargetBaseUrl(e.target.value)}
                      placeholder="https://staging.mystore.com"
                    />
                  </Field>
                  <div className="checkbox-group">
                    <Checkbox
                      checked={syncAnonymizePII}
                      onChange={(e) => setSyncAnonymizePII(e.target.checked)}
                      label="Anonymize customer PII (names, emails, addresses, orders)"
                    />
                    <Checkbox
                      checked={syncDisableEmails}
                      onChange={(e) => setSyncDisableEmails(e.target.checked)}
                      label="Disable outgoing emails (sandbox safety mode)"
                    />
                  </div>
                  <ErrorNotice error={syncError} />
                  {syncResult ? (
                    <Callout tone="success" title="Sync complete">
                      {syncResult.table_count} tables imported in {Math.round(syncResult.duration_ms / 1000)} s.{' '}
                      {syncResult.base_url_updated ? 'Base URLs updated. ' : ''}
                      {syncResult.pii_anonymized ? 'Customer data anonymized. ' : 'Customer data copied as it was. '}
                      The previous database was backed up first (backup <code>{syncResult.checkpoint_backup_id.slice(0, 8)}</code>).
                    </Callout>
                  ) : null}
                  <button
                    className="button button--danger-quiet"
                    type="submit"
                    disabled={syncing || (!syncSourceEnvId && !syncSourceBackupId)}
                  >
                    {syncing ? 'Syncing and anonymizing' : 'Start staging sync'}
                  </button>
                </form>
                <ConfirmDialog
                  open={confirmingSync}
                  title={`Replace the database of ${env.name}?`}
                  description={`The database of ${env.name} is overwritten with a copy of ${
                    syncSourceEnvId
                      ? (siblingEnvironments.data?.environments ?? []).find((e) => e.id === syncSourceEnvId)?.name ?? 'the source environment'
                      : 'the backup'
                  }. Anything stored only in ${env.name} is lost. ${
                    syncAnonymizePII ? 'Customer data is anonymized.' : 'Customer data is copied as it is.'
                  } ${syncDisableEmails ? 'Outgoing email is turned off.' : 'Outgoing email stays on.'} Its current database is backed up first; if that backup fails, nothing is replaced.`}
                  confirmLabel="Replace the database"
                  confirmText={env.kind === 'production' ? env.name : undefined}
                  danger
                  busy={syncing}
                  onConfirm={handleSyncDatabase}
                  onCancel={() => setConfirmingSync(false)}
                />
              </Section>
            ) : null}
          </>
        )}

        {isTab('deployments') && (
          <>
            <Section
              title="Smoke checks"
              description="Requests run against the environment after a deployment switches the release."
              help={
                <p>
                  A check with a full <code>https://</code> URL runs a real GET request. A relative or absolute path has
                  no public domain to resolve against yet, so it stays simulated — every result reports the check’s own
                  expected status back as a pass.
                </p>
              }
              actions={
                <button
                  className="button"
                  type="button"
                  onClick={handleRunSmokeChecks}
                  disabled={runningSmokeChecks || smokeCheckCount === 0}
                >
                  {runningSmokeChecks ? 'Running' : selectedId ? 'Run checks against the selected attempt' : 'Run checks now'}
                </button>
              }
            >
              {smokeChecks.loading ? <LoadingRows label="Loading checks…" rows={2} rowHeight="32px" /> : null}
              <ErrorNotice error={smokeChecks.error} />
              {smokeCheckCount === 0 && !smokeChecks.loading ? (
                <p className="quiet-empty">No smoke checks configured yet.</p>
              ) : (
                <ul className="status-list" aria-label="Smoke checks">
                  {smokeChecks.data?.checks.map((check) => (
                    <li key={check.id} className="status-list__item">
                      <span className="status-list__name">{check.label}</span>
                      <span className="status-list__detail">
                        <code>{check.path}</code>, expects {check.expected_status}
                      </span>
                      <button
                        className="button button--danger-quiet button--small"
                        type="button"
                        aria-label={`Remove the smoke check ${check.label}`}
                        onClick={() => setPendingSmokeCheckDelete({ id: check.id, label: check.label })}
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <Subsection title="Add a check">
                <form className="form" onSubmit={handleCreateSmokeCheck}>
                  <Field label="Path or public HTTPS URL">
                    <Input required value={smokeCheckPath} onChange={(event) => setSmokeCheckPath(event.target.value)} placeholder="/health" />
                  </Field>
                  <Field label="Label">
                    <Input required value={smokeCheckLabel} onChange={(event) => setSmokeCheckLabel(event.target.value)} placeholder="Health" />
                  </Field>
                  <Field label="Expected status">
                    <Input
                      type="number"
                      min={100}
                      max={599}
                      value={smokeCheckExpectedStatus}
                      onChange={(event) => setSmokeCheckExpectedStatus(Number(event.target.value))}
                    />
                  </Field>
                  <ErrorNotice error={smokeCheckError} />
                  <button
                    className="button button--secondary"
                    type="submit"
                    disabled={creatingSmokeCheck || smokeCheckPath.trim() === '' || smokeCheckLabel.trim() === ''}
                  >
                    {creatingSmokeCheck ? 'Saving' : 'Add check'}
                  </button>
                </form>
              </Subsection>
              <ConfirmDialog
                open={pendingSmokeCheckDelete !== null}
                title={`Remove the smoke check “${pendingSmokeCheckDelete?.label ?? ''}”?`}
                description="Later deployments no longer run this check. Results already recorded for earlier attempts stay."
                confirmLabel="Remove check"
                danger
                busy={deletingSmokeCheck}
                onConfirm={() => pendingSmokeCheckDelete && handleDeleteSmokeCheck(pendingSmokeCheckDelete.id)}
                onCancel={() => setPendingSmokeCheckDelete(null)}
              />
            </Section>

            <div className="split-panel">
              <Section
                title="Deploy a commit"
                description={
                  env.live_execution_enabled
                    ? 'Live execution is on: the worker builds this commit, uploads it over SSH, runs the maintenance window and database upgrade if needed, and switches the release for real.'
                    : 'Live execution is off: the attempt is rehearsed step by step and nothing changes on the server. Turn it on under Server and settings to deploy for real.'
                }
              >
                <form className="form" onSubmit={handleStart}>
                  <Field
                    label="Commit SHA"
                    hint="The full 40-character commit identity. The same commit can be deployed again as a new attempt."
                  >
                    <Input
                      required
                      value={commitSHA}
                      onChange={(event) => setCommitSHA(event.target.value)}
                      placeholder="9f2c1ab0d3e4f5a6b7c8d9e0f1a2b3c4d5e6f7a8"
                    />
                  </Field>
                  <Field label="Git ref">
                    <Input value={commitRef} onChange={(event) => setCommitRef(event.target.value)} />
                  </Field>
                  <Field label="Commit message">
                    <Input value={commitMessage} onChange={(event) => setCommitMessage(event.target.value)} />
                  </Field>
                  <ErrorNotice error={startError} />
                  <button className="button" type="submit" disabled={starting || commitSHA.trim() === ''}>
                    {starting ? 'Starting' : env.live_execution_enabled ? 'Deploy' : 'Rehearse deployment'}
                  </button>
                </form>
              </Section>

              <Section title="Deployment attempts">
                {deployments.loading ? <LoadingRows label="Loading attempts…" rows={4} rowHeight="44px" /> : null}
                <ErrorNotice error={deployments.error} />
                {attempts.length === 0 && !deployments.loading ? (
                  <p className="quiet-empty">No deployments</p>
                ) : (
                  <Table label="Deployment attempts" compact>
                    <thead>
                      <tr>
                        <th scope="col">Commit</th>
                        <th scope="col">Status</th>
                        <th scope="col">Attempt</th>
                        <th scope="col">Migration outcome</th>
                        <th scope="col">Recorded</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attempts.map(({ deployment, release }) => (
                        <tr key={deployment.id} {...clickableRow(() => setSelectedId(deployment.id))} className={`table__row--link${deployment.id === selectedId ? ' row--selected' : ''}`}>
                          <th scope="row">
                            <button type="button" className="link-button" aria-pressed={deployment.id === selectedId} onClick={() => setSelectedId(deployment.id)} aria-label={`Show the details of attempt ${deployment.attempt_number}`}>
                              <code>{release.commit_sha.slice(0, 12)}</code>
                            </button>
                            {release.commit_ref ? <div className="muted">{release.commit_ref}</div> : null}
                          </th>
                          <td>
                            <StatusBadge kind="legacyDeployment" value={deployment.status} />
                          </td>
                          <td className="tabular-nums">#{deployment.attempt_number}</td>
                          <td className="muted">{deployment.migration_outcome.replace(/_/g, ' ')}</td>
                          <td className="muted">{new Date(deployment.created_at).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Section>
            </div>

            {selectedId ? (
              <Section
                title="Attempt detail"
                description={
                  selected ? (
                    <>
                      Attempt #{selected.deployment.attempt_number} of <code>{selected.release.commit_sha.slice(0, 12)}</code>
                    </>
                  ) : undefined
                }
                testId="attempt-detail"
              >
                {detail.loading ? <LoadingRows label="Loading attempt…" rows={5} rowHeight="22px" /> : null}
                <ErrorNotice error={detail.error} />
                {selected ? (
                  <>
                    <p className="summary-line">
                      <StatusBadge kind="legacyDeployment" value={selected.deployment.status} />
                    </p>
                    {selected.failure_target_now ? (
                      <p className="panel__note muted">
                        If this attempt failed now it would move to <strong>{selected.failure_target_now}</strong>, based on
                        the rollback policy and the reported migration outcome.
                      </p>
                    ) : null}
                    {selected.deployment.failure_reason ? (
                      <Callout tone="danger" title="What went wrong">
                        {selected.deployment.failure_reason}
                      </Callout>
                    ) : null}

                    <Subsection title="Worker">
                      {selected.job ? (
                        <>
                          <p className="summary-line">
                            <StatusBadge kind="job" value={selected.job.status} />
                            <span className="muted">
                              {selected.job.attempts} attempt{selected.job.attempts === 1 ? '' : 's'}
                            </span>
                          </p>
                          {selected.job.status === 'failed' && selected.job.last_error ? (
                            <Callout tone="danger" title="The worker job failed">
                              {selected.job.last_error}
                            </Callout>
                          ) : null}
                        </>
                      ) : (
                        <p className="quiet-empty">
                          No worker job for this attempt: {selected.deployment.deployment_mode.replace(/_/g, ' ')} does not
                          get one in this milestone.
                        </p>
                      )}
                    </Subsection>

                    {selected.rollback_decisions && selected.rollback_decisions.length > 0 ? (
                      <Subsection
                        title="Rollback decisions"
                      >
                        <ol className="event-log">
                          {selected.rollback_decisions.map((decision) => (
                            <li key={decision.id}>
                              <code>{decision.from_status}</code> to <code>{decision.to_status}</code>
                              <span className="muted">, {new Date(decision.decided_at).toLocaleString()}</span>
                              <div>{decision.reason}</div>
                            </li>
                          ))}
                        </ol>
                      </Subsection>
                    ) : null}

                    {selected.deployment.status === 'recovery_required' ? (
                      <Subsection title="Recovery">
                        {selected.reconciliation ? (
                          <div>
                            <p className="summary-line">
                              <StatusBadge kind="backup" value="reconciled" />
                              <span>
                                {recoveryActionLabels[selected.reconciliation.action]}, by {selected.reconciliation.resolved_by} on{' '}
                                {new Date(selected.reconciliation.resolved_at).toLocaleString()}
                              </span>
                            </p>
                            <p className="panel__note muted">{selected.reconciliation.notes}</p>
                          </div>
                        ) : (
                          <>
                            <Callout tone="danger" title="This attempt is blocking its environment">
                              No new deployment can start here until it is reconciled.
                            </Callout>
                            <form className="form" onSubmit={handleReconcileRecovery}>
                              <Field label="What was actually done">
                                <Select value={recoveryAction} onChange={(event) => setRecoveryAction(event.target.value as RecoveryAction)}>
                                  {(Object.keys(recoveryActionLabels) as RecoveryAction[]).map((value) => (
                                    <option key={value} value={value}>
                                      {recoveryActionLabels[value]}
                                    </option>
                                  ))}
                                </Select>
                              </Field>
                              {recoveryAction === 'restored_from_backup' ? (
                                <Field label="Backup">
                                  <Select value={recoveryBackupId} onChange={(event) => setRecoveryBackupId(event.target.value)}>
                                    <option value="">Select a backup…</option>
                                    {backups.data?.backups.map((backup) => (
                                      <option key={backup.id} value={backup.id}>
                                        {backup.label || backup.id.slice(0, 8)} ({new Date(backup.created_at).toLocaleString()})
                                      </option>
                                    ))}
                                  </Select>
                                </Field>
                              ) : null}
                              <Field label="Notes" hint="Required: describe what was actually done to resolve this attempt">
                                <Textarea required rows={3} value={recoveryNotes} onChange={(event) => setRecoveryNotes(event.target.value)} />
                              </Field>
                              <ErrorNotice error={recoveryError} />
                              <button
                                className="button"
                                type="submit"
                                disabled={
                                  reconciling ||
                                  recoveryNotes.trim() === '' ||
                                  (recoveryAction === 'restored_from_backup' && recoveryBackupId === '')
                                }
                              >
                                {reconciling ? 'Saving' : 'Reconcile and unblock the environment'}
                              </button>
                            </form>
                          </>
                        )}
                      </Subsection>
                    ) : null}

                    <Subsection
                      title="Plan"
                      description={
                        selected.plan && selected.plan_steps
                          ? `The fixed atomic template snapshot (${selected.plan.template_version}) this attempt started with. It stays the same even if the template changes later.`
                          : undefined
                      }
                    >
                      {selected.plan && selected.plan_steps ? (
                        <PlanStepList steps={selected.plan_steps} />
                      ) : (
                        <p className="quiet-empty">
                          No atomic plan for this attempt: {selected.deployment.deployment_mode.replace(/_/g, ' ')} does not
                          have one in this milestone.
                        </p>
                      )}
                    </Subsection>

                    {selected.smoke_check_results && selected.smoke_check_results.length > 0 ? (
                      <Subsection title="Smoke check results">
                        <ul className="status-list status-list--stacked" aria-label="Smoke check results">
                          {selected.smoke_check_results.map((result) => (
                            <li key={result.id} className="status-list__item">
                              <span className="status-list__name">
                                HTTP {result.observed_status_code}
                                {result.real ? null : (
                                  <>
                                    {' '}
                                    <StatusBadge kind="backup" value="simulated" />
                                  </>
                                )}
                              </span>
                              <StatusBadge kind="checkResult" value={result.status} />
                              <span className="status-list__detail">
                                {result.message} {new Date(result.checked_at).toLocaleString()}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </Subsection>
                    ) : null}

                    <Subsection title="Event log">
                      <ol className="event-log">
                        {selected.events.map((event) => (
                          <li key={event.id}>
                            <code>{event.from_status ?? 'created'}</code> to <code>{event.to_status}</code>
                            <span className="muted">, {new Date(event.occurred_at).toLocaleString()}</span>
                            {event.message ? <div>{event.message}</div> : null}
                          </li>
                        ))}
                      </ol>
                    </Subsection>

                    {selected.allowed_next_statuses.length === 0 ? (
                      <p className="quiet-empty">
                        This attempt is finished. Finished attempts are never reopened; a new attempt is recorded instead.
                      </p>
                    ) : (
                      <Subsection title="Record the next lifecycle step">
                        <form className="form" onSubmit={handleTransition}>
                          <Field label="Next status">
                            <Select value={toStatus} onChange={(event) => setToStatus(event.target.value as DeploymentStatus | '')}>
                              <option value="">Select a status…</option>
                              {selected.allowed_next_statuses.map((status) => (
                                <option key={status} value={status}>
                                  {status.replace(/_/g, ' ')}
                                </option>
                              ))}
                            </Select>
                          </Field>
                          <Field
                            label="Reported migration outcome"
                            hint="An incompatible or unknown outcome forces recovery_required; the API rejects anything else."
                          >
                            <Select value={outcome} onChange={(event) => setOutcome(event.target.value as MigrationOutcome | '')}>
                              <option value="">Keep the current outcome ({selected.deployment.migration_outcome.replace(/_/g, ' ')})</option>
                              {(Object.keys(outcomeLabels) as MigrationOutcome[]).map((value) => (
                                <option key={value} value={value}>
                                  {humanize(value)}: {outcomeLabels[value]}
                                </option>
                              ))}
                            </Select>
                          </Field>
                          <Field label="Note">
                            <Input
                              value={transitionMessage}
                              onChange={(event) => setTransitionMessage(event.target.value)}
                              placeholder="What happened in this step"
                            />
                          </Field>
                          <ErrorNotice error={transitionError} />
                          <button className="button button--secondary" type="submit" disabled={toStatus === ''}>
                            Apply transition
                          </button>
                        </form>
                      </Subsection>
                    )}
                  </>
                ) : null}
              </Section>
            ) : null}

            {selectedId ? (
              <DatabasePreflightCard
                deploymentId={selectedId}
                preflight={preflight.data ?? null}
                loading={preflight.loading}
                onRefresh={() => {
                  setReloadToken((t) => t + 1);
                  setPreflightReloadToken((t) => t + 1);
                }}
                canManageDeployments={canManageDeployments}
              />
            ) : null}
          </>
        )}
      </div>
    </section>
  );
}
